import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, KeyRound, Megaphone, TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function MarketerLanding() {
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/manifest-company.json");
    return () => { if (link && prev) link.setAttribute("href", prev); };
  }, []);

  if (user?.kind === "marketer") return <Navigate to="/" replace />;

  const doLogin = async () => {
    if (!username.trim()) { toast.warning("أدخل اسم المستخدم"); return; }
    if (!password) { toast.warning("أدخل كلمة المرور"); return; }
    setBusy(true);
    try {
      const u = await api.marketerLogin(username.trim(), password);
      setUser({ kind: "marketer", ...u });
      toast.success(`مرحباً ${u.name || "بك"} 👋`);
      await checkAuth();
      navigate("/", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تسجيل الدخول"); }
    finally { setBusy(false); }
  };

  const inputCls = "bg-white/95 border-slate-200 text-slate-900 placeholder:text-slate-400 h-11";

  return (
    <div dir="rtl" data-testid="marketer-landing"
      className="relative min-h-screen flex items-center justify-center px-4 py-10 font-body overflow-hidden bg-slate-950">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/4 h-96 w-96 rounded-full bg-emerald-500/25 blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-blue-600/20 blur-[120px]" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="grid h-20 w-20 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 shadow-lg shadow-emerald-600/30 ring-1 ring-white/20">
            <Megaphone className="h-10 w-10 text-white" />
          </div>
          <h1 className="mt-3 font-heading text-3xl font-extrabold text-white tracking-tight">WorkZone</h1>
          <p className="mt-1 text-sm text-slate-400 flex items-center gap-1.5">
            <TrendingUp className="h-4 w-4 text-emerald-400" /> بوابة فريق التسويق
          </p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-slate-800/60 backdrop-blur-xl p-6 shadow-2xl">
          <div className="mb-5 text-center">
            <h2 className="font-heading text-lg font-bold text-white">تسجيل الدخول</h2>
            <p className="mt-1 text-xs text-slate-400">ادخل باسم المستخدم وكلمة المرور التي زوّدك بها مدير المنصة.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300">اسم المستخدم</Label>
              <Input data-testid="marketer-username-input" dir="ltr" placeholder="username" className={inputCls}
                value={username} onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doLogin()} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">كلمة المرور</Label>
              <Input data-testid="marketer-password-input" type="password" className={inputCls} value={password}
                onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doLogin()} />
            </div>
            <Button data-testid="marketer-login-btn" disabled={busy} onClick={doLogin}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 gap-1.5 text-base">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} تسجيل الدخول
            </Button>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">مخصّصة لفريق تسويق WorkZone فقط</p>
      </div>
    </div>
  );
}
